// standardFrame.js — the BUNDLED default frame (the ONE default for instant first paint).
//
// D14 (public_api_v3.md §2/§5): the REAL frame for a voyage comes from the `/api/v3/voyage/preview`
// response, resolved server-side from the existing style-config mechanism. The widget bundles ONE
// default frame purely so it can paint a framed shell INSTANTLY — before the API response lands —
// and as an offline safety net. It is NOT a style catalogue; the prototype's per-style `/frames/`
// CDN fetch was DROPPED (D8). The bundled default carries no coBranding, so it needs no asset fetch.
//
// Keyed by `${size}|${Orientation}`; `bundledDefaultFrame(layout)` picks the default size for the
// requested orientation. The real per-style/size frame from the API replaces this on first response.

export const STANDARD_FRAMES = {
  "30x40|Portrait": {
    "printCanvas": {
      "width": 3543,
      "height": 4724
    },
    "margins": {
      "left": 173,
      "top": 173,
      "right": 173,
      "bottom": 433
    },
    "inset": {
      "x": 173,
      "y": 173,
      "width": 3197,
      "height": 4118
    },
    "orientation": "Portrait",
    "isPortrait": true,
    "layout": {
      "swapTitleAndShipName": false,
      "pageBackgroundColor": "#F8F5F1",
      "bottomLineColor": "#B7835A",
      "textColor": "#203C51",
      "imageLineYoffOffsetAdjustment": 0.006,
      "imageMapTitleYOffsetAdjustment": 0.014,
      "imageShipNameYOffsetAdjustment": 0.014,
      "imageEndDateYOffsetPortraitAdjustment": 0.026,
      "imageEndDateYOffsetLandscapeAdjustment": 0.033,
      "imageDurationYOffsetPortraitAdjustment": 0.037,
      "imageDurationYOffsetLandscapeAdjustment": 0.046,
      "imageMileageYOffsetPortraitAdjustment": 0.047,
      "imageMileageYOffsetLandscapeAdjustment": 0.059,
      "imageTitleFont": "Termina Demi",
      "imageTitleFontWeight": "600",
      "imageTitleFontColor": "#203C51",
      "imageTitleDisplayed": true,
      "imageShipNameFont": "Inter Semibold",
      "imageShipNameFontWeight": "600",
      "imageShipNameFontColor": "#203C51",
      "imageShipNameDisplayed": true,
      "imageJourneyDatesFont": "Inter Regular",
      "imageJourneyDatesFontWeight": "600",
      "imageJourneyDatesFontColor": "#203C51",
      "imageJourneyDatesDisplayed": true,
      "imageDurationFont": "Inter Regular",
      "imageDurationFontWeight": "600",
      "imageDurationFontColor": "#203C51",
      "imageDurationDisplayed": true,
      "imageDistanceFont": "Inter Regular",
      "imageDistanceFontWeight": "600",
      "imageDistanceFontColor": "#203C51",
      "imageDistanceDisplayed": true,
      "imageLineWidth": 3.33,
      "imageTitleSplitLineSpacing": 53.33,
      "imageTitleFontSize": "46.67px",
      "imageShipNameFontSize": "46.67px",
      "imageJourneyDatesFontSize": "36.67px",
      "imageDurationFontSize": "36.67px",
      "imageDistanceFontSize": "36.67px",
      "coBranding": []
    }
  },
  "30x40|Landscape": {
    "printCanvas": {
      "width": 4724,
      "height": 3543
    },
    "margins": {
      "left": 173,
      "top": 173,
      "right": 173,
      "bottom": 433
    },
    "inset": {
      "x": 173,
      "y": 173,
      "width": 4378,
      "height": 2937
    },
    "orientation": "Landscape",
    "isPortrait": false,
    "layout": {
      "swapTitleAndShipName": false,
      "pageBackgroundColor": "#F8F5F1",
      "bottomLineColor": "#B7835A",
      "textColor": "#203C51",
      "imageLineYoffOffsetAdjustment": 0.006,
      "imageMapTitleYOffsetAdjustment": 0.014,
      "imageShipNameYOffsetAdjustment": 0.014,
      "imageEndDateYOffsetPortraitAdjustment": 0.026,
      "imageEndDateYOffsetLandscapeAdjustment": 0.033,
      "imageDurationYOffsetPortraitAdjustment": 0.037,
      "imageDurationYOffsetLandscapeAdjustment": 0.046,
      "imageMileageYOffsetPortraitAdjustment": 0.047,
      "imageMileageYOffsetLandscapeAdjustment": 0.059,
      "imageTitleFont": "Termina Demi",
      "imageTitleFontWeight": "600",
      "imageTitleFontColor": "#203C51",
      "imageTitleDisplayed": true,
      "imageShipNameFont": "Inter Semibold",
      "imageShipNameFontWeight": "600",
      "imageShipNameFontColor": "#203C51",
      "imageShipNameDisplayed": true,
      "imageJourneyDatesFont": "Inter Regular",
      "imageJourneyDatesFontWeight": "600",
      "imageJourneyDatesFontColor": "#203C51",
      "imageJourneyDatesDisplayed": true,
      "imageDurationFont": "Inter Regular",
      "imageDurationFontWeight": "600",
      "imageDurationFontColor": "#203C51",
      "imageDurationDisplayed": true,
      "imageDistanceFont": "Inter Regular",
      "imageDistanceFontWeight": "600",
      "imageDistanceFontColor": "#203C51",
      "imageDistanceDisplayed": true,
      "imageLineWidth": 3.33,
      "imageTitleSplitLineSpacing": 53.33,
      "imageTitleFontSize": "46.67px",
      "imageShipNameFontSize": "46.67px",
      "imageJourneyDatesFontSize": "36.67px",
      "imageDurationFontSize": "36.67px",
      "imageDistanceFontSize": "36.67px",
      "coBranding": []
    }
  }
};

/** Deep-clone the bundled standard frame for size+orientation, or null on a miss. */
export function standardFrame(size, orientation) {
  const f = STANDARD_FRAMES[`${size}|${orientation}`];
  return f ? JSON.parse(JSON.stringify(f)) : null;
}

// The default print size used for the instant first-paint shell. The real frame (any size) arrives
// from the API response and replaces it, so this only needs to read as a plausible framed product.
const DEFAULT_BUNDLED_SIZE = "30x40";

/**
 * The ONE bundled default frame for instant first paint, picked by the requested orientation
 * ("Portrait" | "Landscape"). Returns a fresh deep clone so in-place tweaks never mutate the
 * bundled source. Falls back to Portrait for an unrecognised layout.
 */
export function bundledDefaultFrame(layout) {
  const orientation = layout === "Landscape" ? "Landscape" : "Portrait";
  return standardFrame(DEFAULT_BUNDLED_SIZE, orientation);
}
